import { Box } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import type { Alpha3Code } from 'i18n-iso-countries'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { Button, Icon, IllustratedMessage, Loader, Tooltip, Typography } from '@percent/lemonade'
import { getShortLanguage } from '@percent/workplace-giving/utils/getShortLanguage'
import { getCampaignDetails } from '@percent/workplace-giving/api/campaigns/getCampaignDetails/getCampaignDetails'
import { getCampaignFundraisers } from '@percent/workplace-giving/api/campaigns/getCampaignFundraisers/getCampaignFundraisers'
import { getCampaignOpportunities } from '@percent/workplace-giving/api/campaigns/getCampaignOpportunities/getCampaignOpportunities'
import { getCampaignOrganisations } from '@percent/workplace-giving/api/campaigns/getCampaignOrganisations/getCampaignOrganisations'
import { Description } from '@percent/workplace-giving/common/components'
import { PageContent } from '@percent/workplace-giving/common/components/Layout/PageContent'
import { getCountryCodeFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import {
  useAuth,
  useContentTags,
  useLogger,
  useQuery,
  useInfiniteQuery,
  getCountryNameWithOverride
} from '@percent/workplace-giving/common/hooks'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { mapInfiniteData } from '@percent/workplace-giving/utils/mapInfiniteData'
import { CampaignDetailsSidebar } from './CampaignDetailsSidebar/CampaignDetailsSidebar'
import { CampaignFundraisers } from './CampaignFundraisers/CampaignFundraisers'
import { CampaignOpportunities } from './CampaignOpportunities/CampaignOpportunities'
import { CampaignOrganisations } from './CampaignOrganisations/CampaignOrganisations'
import { CampaignGivingActions } from './CampaignGivingActions/CampaignGivingActions'
import { TargetCountriesPicker } from './TargetCountriesPicker/TargetCountriesPicker'
import { CampaignDraftBanner } from './CampaignDraftBanner/CampaignDraftBanner'
import classes from './Campaign.module.scss'
import { GivingCampaignStatusDto } from '@percent/workplace-giving/api/campaigns/campaign.types'
import { Permissions } from '@percent/workplace-giving/constants/permissions'
import { permission, scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { useRegions } from '@percent/workplace-giving/common/hooks/useRegions/useRegions'
import { usePostHogFeatureFlag } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/workplace-giving/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

export const CAMPAIGN_RELATED_DETAILS_PAGE_SIZE = 100
const DEFAULT_STALE_TIME = 20000

export function Campaign() {
  const { t, i18n } = useTranslation()
  const { campaignId } = useParams()
  const { logError } = useLogger()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { state: authState } = useAuth()
  const queryClient = useQueryClient()
  const defaultCountry = getCountryCodeFromAuthState(authState)!
  const [selectedTargetCountriesFilter, setSelectedTargetCountriesFilter] = useState<Alpha3Code[] | null>(null)
  const has = usePermission()
  const givingCampaignEnabled = has(scope('giving_campaign'))
  const givingActionsEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.WPG_GIVING_ACTIONS)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const { allCountries } = useRegions()
  const contentTags = useContentTags('giving_campaign', campaignId).map(({ name }) => name)

  const { data, isPending, error, refetch } = useQuery(['getCampaignDetails', campaignId!], getCampaignDetails, {
    enabled: !!campaignId
  })

  const campaignTargetCountries = useMemo(() => data?.targetCountries ?? [], [data?.targetCountries])
  const isSingleCountryCampaignOutsideDefault =
    campaignTargetCountries.length === 1 && !campaignTargetCountries.includes(defaultCountry)
  const targetCountriesFilter = useMemo(
    () =>
      selectedTargetCountriesFilter ??
      (isSingleCountryCampaignOutsideDefault ? campaignTargetCountries : [defaultCountry]),
    [campaignTargetCountries, defaultCountry, isSingleCountryCampaignOutsideDefault, selectedTargetCountriesFilter]
  )

  const queriesStaleTime = useMemo(() => {
    return state?.reloadLists ? 0 : DEFAULT_STALE_TIME
  }, [state?.reloadLists])

  const countryName = useMemo(
    () =>
      targetCountriesFilter.length > 1
        ? t('workplace_giving.search.world')
        : getCountryNameWithOverride(targetCountriesFilter[0], getShortLanguage(i18n)),
    [targetCountriesFilter, i18n, t]
  )

  const campaignCountryName = isSingleCountryCampaignOutsideDefault
    ? getCountryNameWithOverride(campaignTargetCountries[0], getShortLanguage(i18n))
    : ''

  const handlePublishSuccess = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['getAccountGivingActions'] })
  }

  useEffect(() => {
    if (!givingCampaignEnabled) {
      navigate(AppRoute.HOME)
    }
  }, [givingCampaignEnabled, navigate])

  const { data: fundraisersData, isFetching: isFundraisersFetching } = useInfiniteQuery(
    [
      'getCampaignFundraisers',
      {
        campaignId: campaignId!,
        pageSize: CAMPAIGN_RELATED_DETAILS_PAGE_SIZE,
        targetCountries: targetCountriesFilter
      }
    ],
    getCampaignFundraisers,
    {
      staleTime: queriesStaleTime,
      enabled: !!campaignId && !!data
    }
  )

  const { data: opportunitiesData, isFetching: isOpportunitiesFetching } = useInfiniteQuery(
    [
      'getCampaignOpportunities',
      {
        campaignId: campaignId!,
        pageSize: CAMPAIGN_RELATED_DETAILS_PAGE_SIZE,
        targetCountries: targetCountriesFilter
      }
    ],
    getCampaignOpportunities,
    {
      staleTime: queriesStaleTime,
      enabled: !!campaignId && !!data
    }
  )

  const { data: organisationsData, isFetching: isOrganisationsFetching } = useInfiniteQuery(
    ['getCampaignOrganisations', { campaignId: campaignId!, pageSize: CAMPAIGN_RELATED_DETAILS_PAGE_SIZE }],
    getCampaignOrganisations,
    {
      staleTime: queriesStaleTime,
      enabled: !!campaignId && !!data
    }
  )

  const mappedFundraisersData = mapInfiniteData(fundraisersData)
  const mappedOpportunitiesData = mapInfiniteData(opportunitiesData)
  const mappedOrganisationsData = mapInfiniteData(organisationsData)
  const showNoResultsMessage = useMemo(() => {
    if (targetCountriesFilter.length > 1) return false

    const hasFundraisers = !!mappedFundraisersData?.pages?.[0]?.length
    const hasOpportunities = !!mappedOpportunitiesData?.pages?.[0]?.length
    const hasOrganisations = !!mappedOrganisationsData?.pages?.[0]?.length

    return !hasFundraisers && !hasOpportunities && !hasOrganisations
  }, [targetCountriesFilter, mappedFundraisersData, mappedOpportunitiesData, mappedOrganisationsData])

  const canManageCampaigns = has(permission(Permissions.CampaignsWrite))
  const isContentLoaded = !isOrganisationsFetching && !isFundraisersFetching && !isOpportunitiesFetching

  useEffect(() => {
    if (
      data &&
      isInitialLoad &&
      !isSingleCountryCampaignOutsideDefault &&
      isContentLoaded &&
      !mappedFundraisersData?.pages?.[0]?.length &&
      !mappedOpportunitiesData?.pages?.[0]?.length &&
      !mappedOrganisationsData?.pages?.[0]?.length
    ) {
      setIsInitialLoad(false)
      setSelectedTargetCountriesFilter(allCountries)
    }
  }, [
    allCountries,
    data,
    isContentLoaded,
    isInitialLoad,
    isSingleCountryCampaignOutsideDefault,
    mappedFundraisersData?.pages,
    mappedOpportunitiesData?.pages,
    mappedOrganisationsData?.pages
  ])

  if (isPending) {
    return <Loader />
  }

  if (error) {
    const isNotFoundError = axios.isAxiosError(error) && error.response?.status === 404

    if (!isNotFoundError) {
      logError(error)
    }

    return (
      <Page testId="campaign-details-page-wrapper-error">
        <Box style={{ width: '100%', textAlign: 'center', paddingTop: '2rem' }}>
          <IllustratedMessage
            illustration="no-results"
            title={t('workplace_giving.errors.pageUnavailable')}
            description={t('workplace_giving.errors.invalidPageUrl')}
            cta={{
              label: t('workplace_giving.common.returnHome'),
              onClick: () => navigate(AppRoute.HOME)
            }}
            ctaShape="oval"
          />
        </Box>
      </Page>
    )
  }

  if (data && data.status === GivingCampaignStatusDto.DRAFT && !canManageCampaigns) {
    return (
      <Page testId="campaign-details-page-wrapper-not-found">
        <Box style={{ width: '100%', textAlign: 'center', paddingTop: '2rem' }}>
          <IllustratedMessage
            illustration="no-results"
            title={t('workplace_giving.errors.pageUnavailable')}
            description={t('workplace_giving.errors.invalidPageUrl')}
          />
          <Box style={{ marginTop: '1.5rem' }}>
            <Button
              variant="primary"
              shape="oval"
              onClick={() => navigate(AppRoute.HOME)}
              data-testid="return-home-button"
            >
              {t('workplace_giving.common.returnHome')}
            </Button>
          </Box>
        </Box>
      </Page>
    )
  }

  return (
    <>
      {data && campaignId && data.status === GivingCampaignStatusDto.DRAFT && canManageCampaigns && (
        <CampaignDraftBanner campaignId={campaignId} onPublishSuccess={handlePublishSuccess} />
      )}
      <Page testId="campaign-details-page-wrapper">
        {data && campaignId ? (
          <>
            <PageContent>
              <Description
                tags={contentTags}
                image={data.coverImage}
                name={data.title}
                story={data.description}
                entityName={t('workplace_giving.common.campaign')}
              />
              <Box className={classes.ActionSectionsWrapper} id="actions-section">
                {(data?.targetCountries?.length ?? 0) > 1 || isSingleCountryCampaignOutsideDefault ? (
                  <Box className={classes.CountryPickerWrapper}>
                    <Typography>{t('workplace_giving.campaigns.countriesPicker.label')}</Typography>
                    <TargetCountriesPicker value={targetCountriesFilter} onChange={setSelectedTargetCountriesFilter} />
                    {isSingleCountryCampaignOutsideDefault ? (
                      <Tooltip
                        content={t('workplace_giving.campaigns.countriesPicker.containsContentFor', {
                          countryName: campaignCountryName
                        })}
                        placement="top"
                      >
                        <div className={classes.CountryContentTooltip} data-testid="tooltip-campaign-country-content">
                          <Icon name="info" size="m" color="neutral300" />
                        </div>
                      </Tooltip>
                    ) : null}
                  </Box>
                ) : null}

                {givingActionsEnabled && (
                  <CampaignGivingActions campaignId={campaignId} targetCountries={targetCountriesFilter} />
                )}

                <CampaignFundraisers
                  campaignId={campaignId}
                  data={mappedFundraisersData}
                  isFetching={isFundraisersFetching}
                />
                <CampaignOpportunities
                  campaignId={campaignId}
                  data={mappedOpportunitiesData}
                  isFetching={isOpportunitiesFetching}
                />
                <CampaignOrganisations
                  campaignId={campaignId}
                  data={mappedOrganisationsData}
                  isFetching={isOrganisationsFetching}
                />
              </Box>
              {showNoResultsMessage && (
                <Box className={classes.SectionWrapper}>
                  <IllustratedMessage
                    illustration="no-results"
                    title={t('workplace_giving.campaigns.countriesPicker.noResults', {
                      countryName
                    })}
                    description={t('workplace_giving.campaigns.countriesPicker.noResultsCTA')}
                  />
                </Box>
              )}
            </PageContent>
            <CampaignDetailsSidebar campaign={data} />
          </>
        ) : null}
      </Page>
    </>
  )
}
